<template lang="pug">
  .width-100.editor-container.column(:class='{fullscreen: showFullscreen}' :style='{height}')
    editor-menu.editor-menu.m4-bottom-1(
      v-if='editor'
      :editor='editor'
      :showFullscreen='showFullscreen'
      :showPreview='showPreview'
      @toggleFullscreen='updateFullscreen'
      @togglePreview='updatePreview'
      :heading='heading'
      :bold='bold'
      :italic='italic'
      :strike='strike'
      :underline='underline'
      :removeMarks='removeMarks'
      :unorderedList='unorderedList'
      :orderedList='orderedList'
      :quote='quote'
      :link='link'
      :removeLink='removeLink'
      :image='image'
      :video='video'
      :table='table'
      :undo='undo'
      :redo='redo'
      :preview='preview'
      :fullscreen='fullscreen'
    )
    .editor.row.item-1
      editor-content.item-1.width-100(:editor="editor")
      .preview.md-content.item-1.m-left-1(v-html='value || ""' v-if='showPreview')
    .characters.text-small.m-top-1(v-if='maxCharacters')
      | {{ editor.storage.characterCount.characters() }}/{{ maxCharacters }} {{ $t("tiptap.characters") }}
    .row.m-top-1(v-if='showError')
      simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
      span.text-small.red
        | {{ errorMessage }}
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import EditorMenu from './TipTapMenu.vue'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Image from './Image'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import CharactersCount from '@tiptap/extension-character-count'
import Iframe from './Iframe'
import inputsValidationMixin from '@/mixins/inputsValidationMixin'
import errorIcon from '@/assets/ic_stop.svg'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'

export default {
  components: { EditorContent, EditorMenu },
  mixins: [inputsValidationMixin],
  props: {
    value: {
      type: String,
      required: true,
      default: ''
    },
    maxCharacters: { type: Number },
    errorMessage: { type: String },
    validations: { type: Object },
    height: { type: String, default: '350px' },
    placeholder: { type: String },
    heading: { type: Boolean },
    bold: { type: Boolean },
    italic: { type: Boolean },
    strike: { type: Boolean },
    underline: { type: Boolean },
    removeMarks: { type: Boolean },
    unorderedList: { type: Boolean },
    orderedList: { type: Boolean },
    quote: { type: Boolean },
    link: { type: Boolean },
    removeLink: { type: Boolean },
    image: { type: Boolean },
    video: { type: Boolean },
    undo: { type: Boolean },
    redo: { type: Boolean },
    table: { type: Boolean },
    preview: { type: Boolean },
    fullscreen: { type: Boolean }
  },
  data() {
    return {
      editor: null,
      showFullscreen: false,
      showPreview: false,
      errorIcon
    }
  },
  methods: {
    updateFullscreen() {
      this.showFullscreen
        ? document.body.style.overflowY = 'auto'
        : document.body.style.overflowY = 'hidden'
      this.showFullscreen = !this.showFullscreen
    },
    updatePreview() {
      this.showPreview = !this.showPreview
    }
  },
  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value
      if (isSame) return
      this.editor.commands.setContent(value, false)
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        Image,
        Table.configure({
          resizable: true
        }),
        TableRow,
        TableHeader,
        TableCell,
        Link.configure({
          autolink: true,
          openOnClick: true,
          linkOnPaste: true
        }),
        CharactersCount.configure({
          limit: this.maxCharacters || null
        }),
        Iframe,
        Placeholder.configure({
          placeholder: this.placeholder
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph']
        })
      ],
      onUpdate: () => {
        const empty = this.editor.isEmpty
        this.$emit('input', empty ? '' : this.editor.getHTML())
      },
      onBlur: () => {
        this.verifyValidation()
      }
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/_colors.scss';
@import 'src/scss/variables/_sizes.scss';
@import 'src/scss/common/texts.scss';
@import 'src/scss/common/markdown.scss';
@use 'sass:math';

.editor{
  overflow-y: auto;
}

.fullscreen{
  background-color: $lighter-gray;
  position: fixed;
  height: 100vh;
  z-index: 99;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding: 10px;
  /deep/ .editor{
    flex: 1;
    overflow-y: auto;
    .ProseMirror{
      height: 100%;
    }
  }
}
/deep/ .preview{
  overflow-y: auto;
  padding: 10px;
}
/deep/ .characters{
  z-index: 2;
  top: 10px;
  right: 25px;
  @extend .text-left;
}

/deep/ .ProseMirror {
  @extend .md-content;
  width: 100%;
  background-color: $white;
  border: 1px solid $gray;
  height: 100%;
  overflow-y: auto;
  padding: 10px;
  border-radius: 5px;
  margin: 0;
  &>.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: $dark-gray;
    pointer-events: none;
    height: 0;
    @extend .text-normal;
  }
  &:focus{
    outline: none;
    border: 1px solid $blue;
  }
  &>*{
    display: block;
  }
  p{
    font-size: 16px;
  }
  a{
    font-size: 16px;
  }
  h1{
    a{
      @extend .title-medium;
    }
  }
  h2{
    a{
      @extend .title-small;
    }
  }
  ul{
    margin-left: 5px;
    li{
      margin: 0;
      &>*{
        display: block;
      }
      &::before{
        margin: 12px 10px 0 0;
      }
    }
  }
  ol{
    padding-left: 20px;
    li{
      margin: 0;
    }
  }
  blockquote{
    &>*{
      display: block;
    }
    background-color: $lighter-gray;
    border-left: 4px solid $gray;
    padding: 5px;
    padding-left: 15px;
    margin: 15px 0;
  }
  .custom-image-container{
    position: relative;
    margin: 35px;
    border: 1px solid $light-gray;
    border-radius: 5px;
    .custom-image-delete{
      border-radius: 50%;
      background-color: $lighter-gray;
      border: $gray 1px solid;
      box-shadow: 0px 0px 10px -10px $red;
      width: 26px;
      height: 26px;
      position: absolute;
      right: -10px;
      top: -10px;
      display: flex;
      place-items: center;
      img{
        width: 18px;
      }
    }
    .custom-image-image {
      max-width: 100%;
      height: auto;
      margin: 0 auto;
    }
  }
  .iframe-wrapper {
    position: relative;
    margin: 5px 0;
    padding-bottom: calc(calc(100 / 16)*9%);
    height: 0;
    overflow: hidden;
    width: 100%;
    height: auto;
    &.ProseMirror-selectednode {
      outline: 3px solid $blue;
    }
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
